import { useEffect, useCallback, useRef } from 'react';

interface UseCopyProtectionOptions {
  /**
   * Whether to enable copy protection (default: true)
   */
  enabled?: boolean;
  /**
   * Whether to show a warning toast when copy is attempted (default: true)
   */
  showWarning?: boolean;
  /**
   * Custom warning message to display
   */
  warningMessage?: string;
  /**
   * Callback when a copy attempt is blocked
   */
  onCopyAttempt?: (method: 'keyboard' | 'contextmenu' | 'copy' | 'cut') => void;
}

interface CopyProtectionHandlers {
  /**
   * Handler for onCopy event - attach to elements that need protection
   */
  onCopy: (e: React.ClipboardEvent) => void;
  /**
   * Handler for onCut event - attach to elements that need protection
   */
  onCut: (e: React.ClipboardEvent) => void;
  /**
   * Handler for onContextMenu event - attach to elements that need protection
   */
  onContextMenu: (e: React.MouseEvent) => void;
  /**
   * CSS style object to apply to protected content
   */
  protectedStyle: React.CSSProperties;
  /**
   * CSS class name for protected content (use with global CSS)
   */
  protectedClassName: string;
}

/**
 * Hook to prevent copying content from protected areas.
 * This is an anti-cheating measure for interview questions.
 *
 * Features:
 * - Blocks Ctrl+C / Cmd+C keyboard shortcuts
 * - Blocks right-click context menu
 * - Blocks programmatic copy/cut events
 * - Provides CSS styles to prevent text selection
 *
 * Usage:
 * ```tsx
 * const { onCopy, onCut, onContextMenu, protectedStyle } = useCopyProtection({
 *   enabled: true,
 *   showWarning: true,
 *   warningMessage: 'Copying interview questions is not allowed'
 * });
 *
 * return (
 *   <div
 *     onCopy={onCopy}
 *     onCut={onCut}
 *     onContextMenu={onContextMenu}
 *     style={protectedStyle}
 *   >
 *     Protected content here
 *   </div>
 * );
 * ```
 */
export function useCopyProtection(options: UseCopyProtectionOptions = {}): CopyProtectionHandlers {
  const {
    enabled = true,
    showWarning = true,
    warningMessage = 'Copying is not allowed during the interview',
    onCopyAttempt,
  } = options;

  // Track if we've already shown a warning recently to avoid spam
  const lastWarningRef = useRef<number>(0);
  const WARNING_COOLDOWN_MS = 3000; // 3 seconds cooldown between warnings

  const showWarningMessage = useCallback((method: 'keyboard' | 'contextmenu' | 'copy' | 'cut') => {
    if (!showWarning) return;

    const now = Date.now();
    if (now - lastWarningRef.current < WARNING_COOLDOWN_MS) return;
    lastWarningRef.current = now;

    // Import toast dynamically to avoid circular dependencies
    import('sonner').then(({ toast }) => {
      toast.warning(warningMessage, {
        duration: 2000,
        icon: '🔒',
      });
    }).catch(() => {
      // Fallback if toast import fails
      console.warn('[CopyProtection]', warningMessage);
    });

    onCopyAttempt?.(method);
  }, [showWarning, warningMessage, onCopyAttempt]);

  // Handle keyboard shortcuts (Ctrl+C, Cmd+C, Ctrl+X, Cmd+X)
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for copy shortcuts (Ctrl+C or Cmd+C)
      const isCopyShortcut = (e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C');
      // Check for cut shortcuts (Ctrl+X or Cmd+X)
      const isCutShortcut = (e.ctrlKey || e.metaKey) && (e.key === 'x' || e.key === 'X');

      if (isCopyShortcut || isCutShortcut) {
        // Check if the target is within a protected area (ActualInterviewPage)
        // Allow copy in input/textarea elements
        const target = e.target as HTMLElement;
        const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        const isContentEditable = target.isContentEditable;

        if (!isInputElement && !isContentEditable) {
          e.preventDefault();
          e.stopPropagation();
          showWarningMessage('keyboard');
        }
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [enabled, showWarningMessage]);

  // Handler for onCopy event
  const onCopy = useCallback((e: React.ClipboardEvent) => {
    if (!enabled) return;

    e.preventDefault();
    e.stopPropagation();
    showWarningMessage('copy');
  }, [enabled, showWarningMessage]);

  // Handler for onCut event
  const onCut = useCallback((e: React.ClipboardEvent) => {
    if (!enabled) return;

    e.preventDefault();
    e.stopPropagation();
    showWarningMessage('cut');
  }, [enabled, showWarningMessage]);

  // Handler for onContextMenu event (right-click)
  const onContextMenu = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;

    // Allow context menu on input/textarea elements
    const target = e.target as HTMLElement;
    const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    const isContentEditable = target.isContentEditable;

    if (!isInputElement && !isContentEditable) {
      e.preventDefault();
      e.stopPropagation();
      showWarningMessage('contextmenu');
    }
  }, [enabled, showWarningMessage]);

  // CSS styles to prevent text selection
  const protectedStyle: React.CSSProperties = enabled ? {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',
    MozUserSelect: 'none',
  } as React.CSSProperties : {};

  return {
    onCopy,
    onCut,
    onContextMenu,
    protectedStyle,
    protectedClassName: enabled ? 'copy-protected' : '',
  };
}

export default useCopyProtection;
